import type { SessionSnapshot } from '@scrambl/shared/session-scope'
import { createSessionScope } from '@scrambl/shared/session-scope'

/** Public correlation metadata, never a credential or an authorization decision. */
export const BROWSER_LOGIN_REVISION_KEY = 'scrambl-browser-login-revision-v1'
const REVISION =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

export function createBrowserLoginScope(options: {
  read(): string | null
  write(revision: string): void
  randomUUID(): string
}) {
  const scope = createSessionScope()
  const listeners = new Set<(remote: boolean) => void>()
  let revision: string | null = null
  let storedRevision: string | null = null
  let memoryOnly = false

  function transition(next: string | null, remote: boolean) {
    if (next === revision) return
    revision = next
    scope.transition(next)
    for (const listener of listeners) listener(remote)
  }
  function synchronize() {
    let raw: string | null
    try {
      raw = options.read()
    } catch {
      // Storage may be disabled. Preserve the current in-memory revision.
      return
    }
    const next = raw && REVISION.test(raw) ? raw : null
    // A failed write still fences this tab. A subsequent external change can
    // replace that fallback, but storage failure cannot offer restart safety.
    if (!memoryOnly || next !== storedRevision) {
      memoryOnly = false
      storedRevision = next
      transition(next, true)
    }
  }
  function capture(): SessionSnapshot {
    synchronize()
    const snapshot = scope.capture()
    return {
      ...snapshot,
      assertCurrent() {
        synchronize()
        snapshot.assertCurrent()
      },
    }
  }
  // Hydrate before accepting subscriptions: booting is not a new login.
  synchronize()
  return {
    capture,
    synchronize,
    subscribe(listener: (remote: boolean) => void) {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
    accept(expected: SessionSnapshot) {
      expected.assertCurrent()
      const next = options.randomUUID()
      if (!REVISION.test(next) || next === revision)
        throw new Error('Invalid login revision.')
      try {
        options.write(next)
        storedRevision = next
        memoryOnly = false
      } catch {
        memoryOnly = true
      }
      transition(next, false)
    },
  }
}

export const browserLoginScope = createBrowserLoginScope({
  read: () => window.localStorage.getItem(BROWSER_LOGIN_REVISION_KEY),
  write: revision =>
    window.localStorage.setItem(BROWSER_LOGIN_REVISION_KEY, revision),
  randomUUID: () => crypto.randomUUID(),
})

if (typeof window !== 'undefined')
  window.addEventListener('storage', event => {
    if (event.key === null || event.key === BROWSER_LOGIN_REVISION_KEY)
      // Read current storage, not an obsolete event queued before another login.
      browserLoginScope.synchronize()
  })
